import router from "src/routes/router";
import store from "./store";
import axios from "axios";
import swal from "sweetalert2";

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL
});

// Redirect to login pages on unauthorized errors
axiosInstance.interceptors.response.use(
  response => response,
  function(error) {
    if (error.isAxiosError && error.response !== undefined) {
      const path = router.currentRoute.path;
      if (
        [401, 403].includes(error.response.status) &&
        !path.includes("login")
      ) {
        if (path.includes("user")) {
          swal.close();
          store.dispatch("auth/logout");
          router.push({ name: "UserLogin" });
        } else {
          router.push({ name: "AdminLogin" });
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * @typedef {Object} JWTToken
 * @property {string} token
 * @property {string | null} refreshToken
 * @property {string} uid
 * @property {string} created_on
 * @property {string} updated_on
 */

/**
 *
 * @typedef {Object} LoginResponse
 * @property {JWTToken} jwtToken
 * @property {number} result
 * @property {import('src/store/modules/auth').User} user
 */

/**
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import('axios').AxiosResponse<LoginResponse>>}
 */
export const $login = (email, password) => {
  return axiosInstance.post("auth/login", {
    email,
    password
  });
};

/**
 * @typedef {"Active" | "Inactive"}  HomeDataStatus
 *
 * @typedef {Object} HomeData
 * @property {string} id
 * @property {number} bit
 * @property {number} cash
 * @property {number} gold
 * @property {HomeDataStatus} status
 * @property {string} time
 * @property {string} createdOn
 * @property {string} updatedOn
 * @property {string} asOfDate
 *
 */

/**
 * @template T
 * @typedef {Object} PaginatedResponse<T>
 * @property {number} page
 * @property {number} pageSize
 * @property {number} totalItems
 * @property {T[]} items
 */

/**
 * @param {Date} [start_date]
 * @param {Date} [end_date]
 *
 * @param {Omit<PaginatedResponse<any>, 'items' | 'totalItems'>} [pagination]
 * @returns {Promise<import("axios").AxiosResponse<PaginatedResponse<HomeData>>>}
 */
export const $allHomeData = (start_date, end_date, pagination) => {
  return axiosInstance.get("/home_data", {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    },
    params: {
      start_date,
      end_date,
      ...pagination
    }
  });
};

/**
 * @returns {Promise<import("axios").AxiosResponse<HomeData>>}
 */
export const $lastHomeData = () => {
  return axiosInstance.get("/home_data/last", {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @param {Pick<HomeData, 'bit' | 'cash'| 'gold'| 'status'>} payload
 */
export const $updateHomeData = payload => {
  return axiosInstance.post("/home_data", payload, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {File} file
 * @param {Date} asOfDate
 */
export const $uploadCSV = (file, asOfDate) => {
  const formdata = new FormData();
  formdata.append("csvFile", file, file.name);
  formdata.append("asOfDate", asOfDate.toUTCString());

  return axiosInstance.post("/home_data/import_csv", formdata, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @returns {Promise<import("axios").AxiosResponse<import("src/store/modules/auth").User[]>>}
 */
export const $getUsers = () => {
  return axiosInstance.get("/users/all", {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @param {String} userId
 *
 * @returns {Promise<import('axios').AxiosResponse<import("src/store/modules/auth").User>>}
 */
export const $getUserById = userId => {
  return axiosInstance.get(`/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Omit<import("src/store/modules/auth").User, 'uid' | 'claims'> & {password : string, trial: boolean}} payload
 *
 * @returns {Promise<import("axios").AxiosResponse<import("src/store/modules/auth").User>>}
 */
export const $addUser = payload => {
  return axiosInstance.post("/auth/signup", payload, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Omit<import("src/store/modules/auth").User, 'email' | 'claims'>} payload
 *
 * @returns {Promise<import("axios").AxiosResponse<import("src/store/modules/auth").User>>}
 */
export const $editUser = payload => {
  const { uid, ...rest } = payload;
  return axiosInstance.put(`/users/${uid}`, rest, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {string} userID
 */
export const $deleteUser = userID => {
  return axiosInstance.delete(`/users/${userID}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @typedef {Object} DailyWeight
 * @property {String} id
 * @property {Number} threshBc
 * @property {Number} threshBg
 * @property {Number} tacGoldModelWgt
 * @property {Number} bitCashModelWgt
 * @property {Number} bitTGLDModelWgt
 * @property {Number} goldSigMthEnd
 * @property {String} time
 */

/**
 * @returns {Promise<import("axios").AxiosResponse<DailyWeight[]>>}
 */
export const $getDailyWeights = () => {
  return axiosInstance.get("daily_weight/all", {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Omit<DailyWeight, 'id'>} payload
 * @returns {Promise<import("axios").AxiosResponse<DailyWeight>>}
 */
export const $addDailyWeight = payload => {
  return axiosInstance.post(`daily_weight`, payload, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {string} id
 * @returns {Promise<import("axios").AxiosResponse<DailyWeight>>}
 */
export const $getDailyWeightById = id => {
  return axiosInstance.get(`/daily_weight/${id}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Omit<DailyWeight, 'time'>} payload
 * @returns {Promise<import("axios").AxiosResponse<DailyWeight>>}
 */
export const $editDailyWeight = payload => {
  const { id, ...rest } = payload;
  return axiosInstance.put(`/daily_weight/${id}`, rest, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {String} id
 */
export const $deleteDailyWeight = id => {
  return axiosInstance.delete(`/daily_weight/${id}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @typedef {Object} MarketData
 * @property {string} id
 * @property {Number} xbtusdCurrencyValue
 * @property {Number} xbtusdCurrencyDailyChange
 * @property {Number} gldusEquityDailyChange
 * @property {Number} spyusEquityDailyChange
 * @property {String} time
 */

/**
 * @returns {Promise<import("axios").AxiosResponse<MarketData[]>>}
 */

export const $getAllMarketData = () => {
  return axiosInstance.get(`market_data`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Omit<MarketData, 'id' >} payload
 */
export const $addMarketData = payload => {
  return axiosInstance.post(`market_data/add`, payload, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {String} id
 * @returns {Promise<import("axios").AxiosResponse<MarketData>>}
 */
export const $getMarketData = id => {
  return axiosInstance.get(`market_data/${id}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @param {Omit<MarketData, 'time'>} payload
 * @returns {Promise<import("axios").AxiosResponse<MarketData>>}
 */
export const $editMarketData = payload => {
  const { id, ...rest } = payload;

  return axiosInstance.put(`market_data/${id}`, rest, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {String} id
 */
export const $deleteMarketData = id => {
  return axiosInstance.delete(`market_data/${id}`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @typedef {Object} AlgShare
 * @property {String} share
 * @property {Number} value
 */

/**
 * @typedef {Object} AlgTable
 * @property {String} calculatedForDataOn
 * @property {AlgShare[]} shares
 */

/**
 * @returns {Promise<import("axios").AxiosResponse<AlgTable>>}
 */
export const $getAlgData = () => {
  return axiosInstance.get(`alg_table`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 * @typedef {object} AdminSettings
 * @property {number} maxHistoryLimit
 */

/**
 * @returns {Promise<import('axios').AxiosResponse<AdminSettings>>}
 */
export const $getAdminSettings = () => {
  return axiosInstance.get(`admin_settings`, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};

/**
 *
 * @param {Partial<AdminSettings>} data
 */
export const $updateAdminSettings = data => {
  return axiosInstance.put(`admin_settings`, data, {
    headers: {
      Authorization: `Bearer ${store.getters["auth/token"]}`
    }
  });
};
